
import React, { useState, useEffect } from 'react';
import { ProductData } from './types/productTypes';
import { Card } from '@/components/ui/card';
import { ProductLinkFilter } from './types';
import VideoHeader from './components/VideoHeader';
import VideoThumbnail from './components/VideoThumbnail';
import VideoContent from './components/VideoContent';
import DialogProductSelector from './components/DialogProductSelector';
import FilterBar from './components/FilterBar';
import EmptyFilterResults from './components/EmptyFilterResults';
import { useVideoLinking } from './hooks/useVideoLinking';
import { useFetchProducts } from './useFetchProducts';

interface ProductsGridProps {
  products: ProductData[];
  profileName?: string;
  selectedProfiles: string[];
}

const ProductsGrid: React.FC<ProductsGridProps> = ({ 
  products: initialProducts, 
  profileName = "Unknown Channel",
  selectedProfiles 
}) => {
  const [filterValue, setFilterValue] = useState<ProductLinkFilter>('all');
  const [products, setProducts] = useState<ProductData[]>(initialProducts);
  
  // Track the current video being processed - moved up before it's referenced
  const [currentVideoId, setCurrentVideoId] = useState<string | null>(null);
  
  const {
    isDialogOpen,
    setIsDialogOpen,
    selectedProductId,
    setSelectedProductId,
    organizationProducts,
    isLoading,
    openProductDialog,
    linkProductToVideo,
    handleOpenVideo,
    shouldRefetch,
    linkedProduct
  } = useVideoLinking();

  // Set up query to refetch data when needed
  const { 
    data: refreshedData,
    refetch 
  } = useFetchProducts(selectedProfiles);

  // When shouldRefetch changes, trigger a refetch
  useEffect(() => {
    if (shouldRefetch) {
      refetch();
    }
  }, [shouldRefetch, refetch]);

  // Update products when refreshedData changes
  useEffect(() => {
    if (refreshedData && refreshedData.products) {
      setProducts(refreshedData.products);
    }
  }, [refreshedData]);

  // Initial setup
  useEffect(() => {
    setProducts(initialProducts);
  }, [initialProducts]);

  // Update product in the list when linkedProduct changes
  useEffect(() => {
    if (linkedProduct && currentVideoId) {
      // Create a new array to trigger re-render
      const updatedProducts = products.map(product => {
        if (product.id === currentVideoId) {
          // Ensure the linkedProduct has the correct shape
          return {
            ...product,
            linkedProduct: {
              id: linkedProduct.id,
              name: linkedProduct.name || linkedProduct.title, // Ensure name is always available
              title: linkedProduct.title,
              image: linkedProduct.image
            }
          };
        }
        return product;
      });
      
      // Explicitly cast to ProductData[] to satisfy TypeScript
      setProducts(updatedProducts as ProductData[]);
    }
  }, [linkedProduct, currentVideoId, products]);

  // Update currentVideoId whenever a product dialog is opened
  const handleOpenProductDialog = (videoId: string) => {
    setCurrentVideoId(videoId);
    openProductDialog(videoId);
  };

  // Filter products based on the selected filter
  const filteredProducts = products.filter(product => {
    if (filterValue === 'all') return true;
    if (filterValue === 'linked') return !!product.linkedProduct;
    if (filterValue === 'unlinked') return !product.linkedProduct;
    return true;
  });

  const resetFilter = () => setFilterValue('all');

  return (
    <>
      <FilterBar filterValue={filterValue} setFilterValue={setFilterValue} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 p-6 pt-6 mt-4 w-full bg-pastel-blueLight dark:bg-[#121624]">
        {filteredProducts.length > 0 ? (
          filteredProducts.map((product) => (
            <Card key={product.id} className="overflow-hidden h-full transition-shadow hover:shadow-md bg-white dark:bg-[#1e2235] border border-gray-200 dark:border-gray-700/30">
              <VideoHeader profileName={profileName} />

              <VideoThumbnail 
                image={product.image}
                title={product.title}
                videoId={product.videoId}
                onVideoClick={() => handleOpenVideo(product.videoId)}
              />

              <VideoContent 
                title={product.title}
                publishedAt={product.publishedAt}
                videoId={product.videoId}
                onOpenVideo={() => handleOpenVideo(product.videoId)}
                onOpenProductDialog={() => handleOpenProductDialog(product.id)}
                isLoading={isLoading}
                linkedProduct={product.linkedProduct}
              />
            </Card>
          ))
        ) : (
          <EmptyFilterResults resetFilter={resetFilter} />
        )}
      </div>

      <DialogProductSelector 
        isOpen={isDialogOpen}
        setIsOpen={setIsDialogOpen}
        selectedProductId={selectedProductId}
        setSelectedProductId={setSelectedProductId}
        organizationProducts={organizationProducts}
        onLinkProduct={linkProductToVideo}
        isLoading={isLoading}
      />
    </>
  );
};

export default ProductsGrid;
